import { Input, Spin, Typography } from 'antd';
import { Dayjs } from 'dayjs';
import { get } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import {
  Checkbox,
  DateInput,
  DocumentInput,
  EmailFormItem,
  EmiratesIdInput,
  NationalitySelect,
  PhoneNumberFormItem,
} from '~/components';

import ContactsDropDown from '~/components/ContactsDropDown';
import RolesSelect from '~/components/inputs/RolesSelect';
import Form from '~/components/WizardForm';
import InputsGroupLayout from '~/layouts/InputsGroupLayout';
import Section from '~/layouts/Section ';
import { ApplicationPayload } from '~/types/ApplicationPayload';
import { Draft } from '~/types/Draft';
import { nameValidatorRulesuSERS } from '~/utils/basicValidation';
import { NameFn } from '~/utils/buildNamePath';
import createUid from '~/utils/createUid';
import { disabledDate18Plus, disabledDateIfLesserThanGivenNumberOfDays } from '~/utils/disableDateRule';
import emailDuplicateCheck from '~/utils/emailValidation';
import emiratesIdValidation from '~/utils/emiratesIdValidation';
import optionalFields from '~/utils/optionalFields';
import { validatePassportNumber } from '~/utils/passportValidation';
import phoneValidation from '~/utils/phoneValidation';

interface Props {
  n: NameFn;
  onFinish: (values: ApplicationPayload) => any;
  initialValues?: any;
  selectedValue?: any;
  index?: any;
  adminType?: string;
  listOfEmails?: string[];
  listOfPhoneNumber?: string[];
  applicationData?: any;
  isSpinning?: boolean;
}

const AdministratorAddForm: React.FC<Props> = (props) => {
  const {
    onFinish,
    isSpinning,
    initialValues,
    n,
    selectedValue,
    adminType,
    listOfEmails,
    listOfPhoneNumber,
    index,
    applicationData,
  } = props;
  const { t } = useTranslation('administrator');
  const { t: tCommon } = useTranslation('common');
  const [form] = Form.useForm();
  const [nationalitySelected, setNationalitySelected] = useState<string>('');
  const users = Form.useWatch('users', form);
  const initiatorEmail = applicationData?.payload?.initiatorEmail.toLowerCase();
  const { usersOptional } = optionalFields;
  const state = useSelector((state: any) => state?.state);
  const { selectedContact, isCopyingBtnPressed } = state;
  const newUid = createUid();

  const isInputFilled = (required: string, optional: string) => {
    const fields =
      users &&
      Object.values(users[0]?.data[required])
        ?.filter((value: any) => value)
        ?.filter((value: any) => value?.length > 0)?.length > 0 &&
      users &&
      !(
        Object.values(users[0]?.data[optional])
          ?.filter((value: any) => value)
          ?.filter((value: any) => value?.length > 0)?.length > 0
      );
    return fields;
  };

  const passportFilled = isInputFilled('passport', 'emiratesId');
  const eidFilled = isInputFilled('emiratesId', 'passport');

  useEffect(() => {
    setNationalitySelected(form.getFieldValue(n(['data', 'nationality'])));
    form.setFields([{ name: 'filterStatus', value: undefined }]);
  }, [form, n]);

  const userTypeOptions = [
    { label: t('administrator.modal.form.options.virtualCardPortal'), value: 'virtualCardHolderPortal' },
    { label: t('administrator.modal.form.options.dataExpenseManagementPortal'), value: 'dataExpenseManagement' },
  ];
  const isItSoftRejectionCase = applicationData?.errorKeys?.length;
  const roleValue = adminType === 'selfServiceUsers' ? 'sspUser' : 'virtualUser';

  useEffect(() => {
    if (selectedContact && Object?.values(selectedContact)?.length) {
      const mutateObj = { ...selectedContact };
      form.resetFields();
      form.setFieldsValue({
        users: [
          {
            data: {
              ...mutateObj,
            },
          },
        ],
      });
    }
  }, [selectedContact, form, roleValue]);

  const showFields = !!Object.keys(selectedContact || {})?.length && isCopyingBtnPressed;
  return (
    <>
      <div style={{ marginBottom: '30px' }}>
        {isCopyingBtnPressed && (
          <ContactsDropDown excludedRoles={adminType === 'selfServiceUsers' ? 'sspUser' : 'virtualUser'} />
        )}
      </div>
      {(showFields || !isCopyingBtnPressed) && (
        <Spin spinning={isSpinning} tip="Loading...">
          <Container className="AdministratorAdd">
            <Form<Draft<'users'>>
              stepKey="users"
              form={form}
              name={formName}
              onFinish={onFinish}
              onValuesChange={() => {}} // Overriding default behavior
              initialValues={{ users: [{ data: { ...selectedValue } }] }}
              // initialValues={{
              //   users: [
              //     {
              //       data: {
              //         firstName: 'aaa',
              //         lastName: 'bbb',
              //         phoneNo: '+971551235432',
              //         mobileNo: '+971551235432',
              //         email: 'aaabbb@yopmail.com',
              //         passport: {
              //           documentNumber: '1111111',
              //           expiryDate: '2026-01-01',
              //           files: [
              //             {
              //               id: 'MTcyM2MwMDUtNTFiYi00MWVlLWI4ZDUtZDcyNDFlYmZhZDgzZGU5MzcwMDEtNTM4Zi00MzM2LWI1NjktZmMzYTU0MTUzODk4P2FjY2Vzc0tleT02Y2Y5MDM1N2E1YjI4YjdhOTU0NmU3YjdmZDkyMWQwYjZhMzYyNTFhM2MwNWE2MjIxNzU0YzA2NjYyMmQ1M2U5',
              //               mimeType: 'image/png',
              //               name: 'ksaVDI_2025-01-08-095208.png',
              //               container: 'sob-ni-1817',
              //             },
              //           ],
              //         },
              //         emiratesId: {},
              //         nationality: 'Jordanian',
              //         dateOfBirth: '1990-01-01',
              //         role: ['Admin'],
              //       },
              //     },
              //   ],
              // }}
              withAutoSaveDraft={false}
              getOptionalFields={(initialValues) => usersOptional(initialValues, passportFilled, eidFilled)}
            >
              <>
                <Form.Item name={n(['data', 'uid'])} hidden initialValue={get(initialValues, n(['data', 'uid']))} />
                <Form.Item
                  name={n(['data', 'contactId'])}
                  hidden
                  initialValue={get(initialValues, n(['data', 'contactId'])) || newUid}
                />
                <Form.Item
                  initialValue={get(initialValues, n(['data', 'salesforceFields']))}
                  hidden
                  name={n(['data', 'salesforceFields'])}
                />
                <Form.Item hidden valuePropName="checked" index={index} name={n(['data', 'salesForceUser'])}>
                  <Checkbox />
                </Form.Item>
                <Form.Item hidden index={index} name={n(['data', 'userRoles'])} />

                {/* Personal Details Section */}
                <Section>
                  <Typography.Title level={4}>
                    {t('administrator.modal.form.personal_data.title').toUpperCase()}
                  </Typography.Title>
                  <InputsGroupLayout>
                    <Form.Item
                      index={index}
                      adminType={adminType}
                      label={t('administrator.modal.form.personal_data.first_name')}
                      name={n(['data', 'firstName'])}
                      rules={[
                        ...nameValidatorRulesuSERS,
                        {
                          required: true,
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      adminType={adminType}
                      label={t('administrator.modal.form.personal_data.second_name')}
                      index={index}
                      name={n(['data', 'secondName'])}
                      rules={nameValidatorRulesuSERS}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      adminType={adminType}
                      label={t('administrator.modal.form.personal_data.last_name')}
                      index={index}
                      name={n(['data', 'lastName'])}
                      rules={[
                        ...nameValidatorRulesuSERS,
                        {
                          required: true,
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                    <PhoneNumberFormItem
                      adminType={adminType}
                      name={n(['data', 'phoneNo'])}
                      index={index}
                      label={t('administrator.modal.form.personal_data.phone_no')}
                      rules={
                        isItSoftRejectionCase
                          ? [{ required: true }]
                          : [
                              { required: true },
                              {
                                validator: phoneValidation(),
                              },
                            ]
                      }
                    />
                    <PhoneNumberFormItem
                      adminType={adminType}
                      name={n(['data', 'mobileNo'])}
                      index={index}
                      label={t('administrator.modal.form.personal_data.mobile_no')}
                      rules={
                        isItSoftRejectionCase
                          ? [{ required: true }]
                          : [
                              { required: true },
                              {
                                validator: phoneValidation(),
                              },
                            ]
                      }
                    />
                    <EmailFormItem
                      adminType={adminType}
                      name={n(['data', 'email'])}
                      index={index}
                      label={`${t('administrator.modal.form.personal_data.email_id')}`}
                      rules={[
                        { required: true },
                        { type: 'email', message: tCommon('Invalid email', { ns: 'validation' }) },
                        { max: 40, message: tCommon('Email must be less than 40 characters', { ns: 'validation' }) },
                        {
                          // validator: emailDuplicateCheck(listOfEmails || [], form.getFieldValue(n(['data', 'email']))),
                          validator: emailDuplicateCheck(
                            form.getFieldValue(n(['data', 'contactId'])),
                            adminType === 'selfServiceUsers' ? 'sspUser' : 'virtualUser',
                          ),
                        },
                      ]}
                    />
                    <Form.Item
                      adminType={adminType}
                      label={t('administrator.modal.form.personal_data.dob')}
                      index={index}
                      name={n(['data', 'dateOfBirth'])}
                      rules={[{ required: true }]}
                    >
                      <DateInput disableDateRule={disabledDate18Plus} />
                    </Form.Item>
                    <Form.Item
                      adminType={adminType}
                      label={t('administrator.modal.form.personal_data.nationality')}
                      index={index}
                      name={n(['data', 'nationality'])}
                      rules={[{ required: true }]}
                    >
                      <NationalitySelect
                        defaultValue={selectedValue?.nationality}
                        onSelect={(value) => {
                          setNationalitySelected(value);
                        }}
                      />
                    </Form.Item>
                  </InputsGroupLayout>
                </Section>

                {/* Passport */}
                <Section>
                  <Typography.Title level={4}>
                    {t('administrator.modal.form.passport.title').toUpperCase()}
                  </Typography.Title>
                  <InputsGroupLayout>
                    <Form.Item
                      adminType={adminType}
                      label={t('administrator.modal.form.passport.passport_no')}
                      index={index}
                      name={n(['data', 'passport', 'documentNumber'])}
                      validateFirst
                      rules={[{ required: !eidFilled }, { validator: validatePassportNumber(eidFilled) }]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      adminType={adminType}
                      label={t('administrator.modal.form.passport.passport_expiry_date')}
                      index={index}
                      name={n(['data', 'passport', 'expiryDate'])}
                      rules={[{ required: !eidFilled }]}
                    >
                      <DateInput
                        disableDateRule={(current: Dayjs) => disabledDateIfLesserThanGivenNumberOfDays(current, 3)}
                      />
                    </Form.Item>
                  </InputsGroupLayout>

                  <Form.Item
                    adminType={adminType}
                    name={n(['data', 'passport', 'files'])}
                    index={index}
                    rules={[{ required: !eidFilled }]}
                  >
                    <DocumentInput name="document" variant="default" />
                  </Form.Item>
                </Section>

                {/* EID */}
                <Section>
                  <Typography.Title level={4}>{`${t(
                    'administrator.modal.form.eid.title',
                  ).toUpperCase()}`}</Typography.Title>
                  <InputsGroupLayout>
                    <Form.Item
                      adminType={adminType}
                      index={index}
                      label={`${t('administrator.modal.form.eid.eid_no')}`}
                      name={n(['data', 'emiratesId', 'documentNumber'])}
                      rules={[{ required: !passportFilled }, { validator: emiratesIdValidation }]}
                    >
                      <EmiratesIdInput />
                    </Form.Item>
                    <Form.Item
                      adminType={adminType}
                      index={index}
                      label={`${t('administrator.modal.form.eid.eid_expiry_date')}`}
                      name={n(['data', 'emiratesId', 'expiryDate'])}
                      rules={[{ required: !passportFilled }]}
                    >
                      <DateInput
                        disableDateRule={(current: Dayjs) => disabledDateIfLesserThanGivenNumberOfDays(current, 3)}
                      />
                    </Form.Item>
                  </InputsGroupLayout>
                  <Form.Item
                    adminType={adminType}
                    name={n(['data', 'emiratesId', 'files'])}
                    index={index}
                    rules={[{ required: !passportFilled }]}
                  >
                    <DocumentInput name="document" variant="default" />
                  </Form.Item>
                </Section>

                {/* Role Section */}
                {/* Only Admin will be the role and visible only when SSP user added */}
                {adminType === 'selfServiceUsers' && (
                  <Section>
                    <Typography.Title level={4}>
                      {t('administrator.modal.form.role.title').toUpperCase()}
                    </Typography.Title>
                    {/* <InputsGroupLayout> */}
                    <Form.Item
                      adminType={adminType}
                      // disabled
                      name={n(['data', 'role'])}
                      index={index}
                      rules={[{ required: true }]}
                      // initialValue="Admin"
                      style={{ width: '60%' }}
                    >
                      <RolesSelect />
                    </Form.Item>
                    {/* </InputsGroupLayout> */}
                  </Section>
                )}

                {/* Type of User */}
                {/* {adminType !== 'selfServiceUsers' && <Section>
          <Typography.Title level={4}>{t('administrator.modal.form.user.type.title').toUpperCase()}</Typography.Title>
          <InputsGroupLayout>
            <CheckboxGroupFormItem
              label=" "
              name={n(['data', 'type'])}
              options={userTypeOptions}
              form={form}
            />
          </InputsGroupLayout>
        </Section>} */}
              </>
            </Form>
          </Container>
        </Spin>
      )}
    </>
    // </FormItemContextProvider>
  );
};

export const formName = 'addNewAdministrator';

const Container = styled.div`
  .test {
    background-color: grey;
  }
`;

export default AdministratorAddForm;
